import { useEffect } from 'react';

/**
 * useReferralCapture — Mar 3, 2026
 * ---------------------------------
 * On landing / first mount, checks the URL for a `?ref=<code>` param.
 * If present:
 *   1. POSTs to /api/referrals/track-click (fire-and-forget, anonymous —
 *      no PII collected; count-only telemetry per BAA transient posture)
 *   2. Stashes the code in localStorage with a 30-day expiry so that
 *      when the user later signs up (register or Google OAuth), the
 *      AuthProvider can forward it to the backend for attribution.
 *   3. Strips the query param from the URL so it doesn't linger in
 *      shared screenshots or bookmark URLs.
 */
const REF_STORAGE_KEY = 'writeright_referral_code';
const REF_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const useReferralCapture = () => {
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const code = (url.searchParams.get('ref') || '').trim().toLowerCase();
      if (!code || !/^[a-z0-9]{4,32}$/.test(code)) return;

      // Persist for signup attribution later
      const payload = { code, expires: Date.now() + REF_TTL_MS };
      try { localStorage.setItem(REF_STORAGE_KEY, JSON.stringify(payload)); } catch { /* ignore */ }

      // Fire-and-forget click ping
      const api = (window.__WR_API_BASE__ || '') + '/api/referrals/track-click';
      fetch(api, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
        // No credentials — this is anonymous
      }).catch(() => { /* silent — click tracking is best-effort */ });

      // Strip ?ref= from the visible URL (leave other params intact)
      url.searchParams.delete('ref');
      const clean = url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : '') + url.hash;
      window.history.replaceState({}, '', clean);
    } catch { /* ignore malformed URL */ }
  }, []);
};

export default useReferralCapture;
